import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart, eur } from "@/context/CartContext";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";

export default function LojaSucesso() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState("checking"); // checking | paid | pending | error
    const [order, setOrder] = useState(null);
    const { clear } = useCart();
    const tries = useRef(0);
    const cleared = useRef(false);

    useEffect(() => {
        if (!sessionId) { setState("error"); return; }
        let timer;
        const poll = async () => {
            try {
                const { data } = await api.get(`/shop/order/status/${sessionId}`);
                setOrder(data);
                if (data.payment_status === "paid") {
                    setState("paid");
                    if (!cleared.current) { clear(); cleared.current = true; }
                    return;
                }
                if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
                tries.current += 1;
                if (tries.current > 8) { setState("pending"); return; }
                timer = setTimeout(poll, 2000);
            } catch {
                tries.current += 1;
                if (tries.current > 8) { setState("error"); return; }
                timer = setTimeout(poll, 2000);
            }
        };
        poll();
        return () => timer && clearTimeout(timer);
        // `clear` intentionally omitted: cleared.current guard already prevents duplicates
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId]);

    return (
        <div className="container-x py-24 flex justify-center" data-testid="loja-sucesso">
            <div className="max-w-md w-full rounded-2xl border border-border bg-white p-8 text-center">
                {state === "checking" && (<><Loader2 className="h-10 w-10 mx-auto animate-spin text-primary" /><h1 className="mt-4 font-heading text-2xl font-extrabold text-primary">A confirmar o pagamento…</h1><p className="mt-2 text-sm text-muted-foreground">Um instante, estamos a validar com a Stripe.</p></>)}
                {state === "paid" && (<><CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500" /><h1 className="mt-4 font-heading text-2xl font-extrabold text-primary">Pagamento confirmado!</h1><p className="mt-2 text-sm text-muted-foreground">Obrigado pela sua encomenda. Enviámos os detalhes para o seu email.</p>{order ? (
                    <div className="mt-4 rounded-xl border border-border bg-muted/50 p-4 text-left">
                        {order.subtotal_cents != null && (
                            <div className="flex items-center justify-between text-sm text-muted-foreground">
                                <span>Subtotal</span>
                                <span>{eur(order.subtotal_cents)}</span>
                            </div>
                        )}
                        {order.vat_cents != null && (
                            <div className="mt-1 flex items-center justify-between text-sm text-muted-foreground">
                                <span>IVA ({Math.round((order.vat_rate || 0) * 100)}%)</span>
                                <span>{eur(order.vat_cents)}</span>
                            </div>
                        )}
                        <div className="mt-2 pt-2 border-t border-border flex items-center justify-between">
                            <span className="font-semibold text-primary">Total pago</span>
                            <span className="font-heading text-xl font-extrabold text-primary">{eur(order.amount_cents)}</span>
                        </div>
                    </div>
                ) : null}</>)}
                {state === "pending" && (<><Loader2 className="h-10 w-10 mx-auto animate-spin text-primary" /><h1 className="mt-4 font-heading text-2xl font-extrabold text-primary">Pagamento em processamento</h1><p className="mt-2 text-sm text-muted-foreground">Assim que for confirmado receberás um email. Podes fechar esta página.</p></>)}
                {state === "error" && (<><AlertTriangle className="h-12 w-12 mx-auto text-amber-500" /><h1 className="mt-4 font-heading text-2xl font-extrabold text-primary">Não conseguimos confirmar</h1><p className="mt-2 text-sm text-muted-foreground">Se o valor foi cobrado, entra em contacto connosco.</p></>)}
                <Link to="/loja" className="mt-6 inline-flex items-center gap-2 rounded-full bg-secondary px-5 py-2.5 text-sm font-bold text-secondary-foreground">Voltar à loja</Link>
            </div>
        </div>
    );
}
